/**
 * Flow type resolution and wizard context building
 * Determines which wizard flow variant to use based on property state and feature flags.
 */

import propertyUtils from '../../../utils/propertyUtils';

import { FlowType as FlowTypeEnum } from '../types';

import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FeatureFlags, FlowType, WizardContext } from '../types';

export function resolveFlowType(
  property: GetUserPropertyDetailsResponseWebDto,
  featureFlags: Partial<FeatureFlags>,
  searchParams: URLSearchParams,
): FlowType {
  // 1. Multiplex flow (highest priority)
  const multiplexSessionId = searchParams.get('multiplexBuildingSessionId');
  if (multiplexSessionId && featureFlags.canAccessMultiplexListingFlow) {
    return FlowTypeEnum.MULTIPLEX;
  }

  // 2. Relisting flow
  if (featureFlags.canAccessRelistingFlow) {
    return featureFlags.canAccessPostingPathTourScheduling ? FlowTypeEnum.RELISTING_TOUR : FlowTypeEnum.RELISTING;
  }

  // 3. Room for rent flow (with variants based on forRentBy)
  if (propertyUtils.isRoomForRent({ listing: property })) {
    const forRentBy = property.forRentBy;
    // RYAN_FLOW: Owner living in property or tenant subletting
    if (forRentBy === 'ownerResident' || forRentBy === 'tenant') {
      return FlowTypeEnum.ROOM_FOR_RENT_RYAN;
    }
    // IRENE_FLOW: Owner not living in property or agent
    return FlowTypeEnum.ROOM_FOR_RENT_IRENE;
  }

  // 4. AI listing creation flow (standard variant with combined steps, not r4r)
  if (featureFlags.canAccessAIListingCreation && featureFlags.canAccessScreeningCriteria) {
    return FlowTypeEnum.AI_LISTING_CREATION;
  }

  // 5. Standard flow (default)
  return FlowTypeEnum.STANDARD;
}

/**
 * Builds complete wizard context from property data and feature flags
 */
export function buildWizardContext(
  property: GetUserPropertyDetailsResponseWebDto,
  featureFlags: FeatureFlags,
  searchParams: URLSearchParams,
): WizardContext {
  const flowType = resolveFlowType(property, featureFlags, searchParams);

  return {
    flowType,
    property,
    flags: featureFlags,
    restrictions: {
      isApplicationsUpdateRestricted: property.restrictions?.applications?.isUpdateRestricted ?? false,
      isHidePhoneRestricted: property.restrictions?.isHidePhoneRestricted ?? false,
    },
    userSelections: {},
  };
}
